
import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useOrganization } from "@/context/OrganizationContext";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Organization = {
  id: string;
  name: string;
};

export function OrganizationListbox() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { currentOrganization, setCurrentOrganization } = useOrganization();
  const { subscriptionData } = useAuth();

  useEffect(() => {
    async function fetchOrganizations() {
      try {
        // Só buscar organizações se tiver subscription_id
        if (!subscriptionData.subscription_id) {
          setOrganizations([]);
          setCurrentOrganization(null);
          return;
        }

        const { data, error } = await supabase
          .from("organizations")
          .select("id, name")
          .eq("subscription_id", subscriptionData.subscription_id)
          .order("name");

        if (error) {
          throw error;
        }

        const orgsData = data?.map(org => ({
          id: org.id,
          name: org.name || ""
        })) || [];
        
        setOrganizations(orgsData);
        
        // Se há organizações e nenhuma está selecionada, selecionar a primeira
        if (orgsData && orgsData.length > 0 && !currentOrganization) {
          setCurrentOrganization(orgsData[0]);
        }
      } catch (error: any) {
        toast({
          title: "Erro ao carregar organizações",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    }

    fetchOrganizations();
  }, [toast, setCurrentOrganization, subscriptionData.subscription_id]);

  const handleOrganizationChange = (value: string) => {
    const selectedOrg = organizations.find(org => org.id === value);
    if (selectedOrg) {
      setCurrentOrganization(selectedOrg);
    }
  };

  return (
    <div className="w-full px-2 mb-4">
      <Select
        disabled={loading}
        value={currentOrganization?.id || ""}
        onValueChange={handleOrganizationChange}
      >
        <SelectTrigger className="w-full bg-sidebar-accent/10 border-sidebar-border text-sidebar-foreground">
          <SelectValue placeholder={loading ? "Carregando..." : "Selecione uma organização"} />
        </SelectTrigger>
        <SelectContent className="bg-popover border-sidebar-border">
          {organizations.length === 0 ? (
            <div className="py-2 px-2 text-sm text-muted-foreground">
              Nenhuma organização encontrada.
            </div>
          ) : (
            organizations.map((org) => (
              <SelectItem key={org.id} value={org.id}>
                {org.name}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    </div>
  );
}
